import React, { ReactNode } from 'react';
import { Box, Flex, Typography, Tooltip } from '@fullstory/components';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';
import { Link } from 'react-router-dom';
import { ArchiveIndicator } from '../ArchiveIndicator';

interface CardTitleProps {
  title: string;
  description?: ReactNode;
  linkTo?: string;
  dateDisplay?: ReactNode;
  minimal?: boolean;
  inverse?: boolean;
  showTitleInTooltip?: boolean;
  isCanned?: boolean;
  isArchived?: boolean;
}

export const CardTitle: React.FC<CardTitleProps> = function CardTitle({
  linkTo,
  title,
  description,
  dateDisplay,
  minimal = false,
  inverse = false,
  showTitleInTooltip = true,
  isCanned = false,
  isArchived = false,
}) {
  const textColor = inverse ? 'neutrals-n0' : 'neutrals-n10';
  let descriptionContent = description;
  if (description && typeof description === 'string') {
    descriptionContent = (
      <Typography
        variant="body3"
        color={minimal ? 'neutrals-n0' : textColor}
        className="fs-unmask"
        overflowWrap="anywhere"
      >
        {description}
      </Typography>
    );
  }
  const titleComponent = (
    <Typography
      className="card-title fs-unmask"
      variant="heading3"
      color={textColor}
      underline={minimal ? 'dashed' : undefined}
    >
      {title}
    </Typography>
  );

  if (minimal) {
    return (
      <Tooltip
        tooltipContent={
          <Flex column alignItems="flex-start">
            {showTitleInTooltip && (
              <Box>
                <Typography variant="heading5" color="neutrals-n0">
                  {title}
                </Typography>
              </Box>
            )}
            {dateDisplay && <Box>{dateDisplay}</Box>}
            {descriptionContent && (
              <Box marginTop={showTitleInTooltip ? 16 : 0}>{descriptionContent}</Box>
            )}
          </Flex>
        }
        variant="dark"
        placement="top-start"
        maxWidth={400}
      >
        <Flex justifyContent="flex-start" alignItems="flex-start">
          <Box
            className={classNames(css.noWrap, css.noUnderline, {
              [css.inverse]: inverse,
            })}
          >
            {linkTo && !isCanned ? <Link to={linkTo}>{titleComponent}</Link> : titleComponent}
          </Box>
          {isArchived && <ArchiveIndicator inverse={inverse} />}
        </Flex>
      </Tooltip>
    );
  }
  return (
    <Flex data-component="CardTitle" column alignItems="flex-start" className={css.noUnderline}>
      {linkTo && !isCanned ? <Link to={linkTo}>{titleComponent}</Link> : titleComponent}
      {descriptionContent && <Box marginTop={8}>{descriptionContent}</Box>}
    </Flex>
  );
};
